import { OAuthProviders, OneTapPositions, Products } from '@stytch/vanilla-js';

import { MINUTES_IN_HALF_HOUR } from './datetime';

import type { IDictionary } from '@/types';
import type { StytchLoginConfig } from '@stytch/vanilla-js';

// 60 days in minutes
export const DEFAULT_SESSION_DURATION_MINUTES = 86400;

// Non-production environments get their own cookie domain so their cookies
// never reach production (.mento.co).
const ENVIRONMENT_SUBDOMAINS = ['staging', 'dev'];

/**
 * Returns cookie options for Stytch SDK initialization.
 *
 * Sets the cookie domain to the parent domain (e.g. `.mento.co`) so that
 * SDK-written cookies use the same domain as the backend HttpOnly cookies
 * (controlled by the COOKIE_DOMAIN env var). Without this, the SDK writes
 * cookies scoped to the exact hostname (e.g. `app.mento.co`) while the
 * backend writes to `.mento.co`, creating duplicate cookies that break
 * MFA verification and session continuity.
 *
 * Returns undefined for localhost so local dev is unaffected.
 */
export const getStytchCookieOptions = ():
  | { domain: string; path: string; availableToSubdomains: boolean }
  | undefined => {
  const { hostname } = window.location;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  const parts = hostname.split('.');
  if (parts.length < 2) return undefined;
  // The SDK cookie domain must match the backend COOKIE_DOMAIN env var to avoid
  // duplicate cookies that break session handoff and login.
  // Production: app.mento.co / moments.mento.co → .mento.co
  // Staging: staging.mento.co / moments.staging.mento.co → .staging.mento.co
  // Development: dev.mento.co → .dev.mento.co
  const isEnvironmentScoped = ENVIRONMENT_SUBDOMAINS.some((label) =>
    parts.includes(label),
  );
  const parentDomain = `.${parts.slice(isEnvironmentScoped ? -3 : -2).join('.')}`;
  return { availableToSubdomains: true, domain: parentDomain, path: '/' };
};

export const STYTCH_STYLES = {
  buttons: {
    primary: {
      backgroundColor: '#637A6F',
      textColor: '#FFF',
      width: '100%',
    },
  },
  colors: {
    error: '#BC4636',
    primary: '#262626',
    secondary: '#262626',
    success: '#637A6F',
  },
  fontFamily: 'Basis Grotesque',
  logo: {
    logoImageUrl:
      'https://mento-space.nyc3.cdn.digitaloceanspaces.com/logo.svg',
  },
};

// Stytch message catalog overrides (messages/b2b/en.po). The forgot-password
// step also signs people up, and it asks for the email before sending one.
export const STYTCH_B2B_STRINGS = {
  'password.forgot.title': 'Sign up or reset password',
};

export const STYTCH_CONFIG = (
  _redirect: string | null,
  oauthOnly: boolean = false,
): StytchLoginConfig => {
  const redirectUrl = import.meta.env.VITE_STYTCH_AUTHENTICATE_URL;

  // For some reason using the redirect in the reset url doesn't set all of the
  // proper cookies. I have no idea why and haven't been able to debug it -
  // configuring in stytch dashboard, making sure config is consistent, and everything
  // else I've tried had no effect.
  // There are some issues with the reset flow, like doing redirects, it might be due to
  // Stytch's component itself
  const resetUrl = import.meta.env.VITE_STYTCH_RESET_URL;

  return {
    emailMagicLinksOptions: {
      loginExpirationMinutes: MINUTES_IN_HALF_HOUR,
      loginRedirectURL: redirectUrl,
      signupExpirationMinutes: MINUTES_IN_HALF_HOUR,
      signupRedirectURL: redirectUrl,
    },
    oauthOptions: {
      loginRedirectURL: redirectUrl,
      providers: [
        {
          one_tap: true,
          position: OneTapPositions.embedded,
          type: OAuthProviders.Google,
        },
      ],
      signupRedirectURL: redirectUrl,
    },
    passwordOptions: {
      loginExpirationMinutes: MINUTES_IN_HALF_HOUR,
      loginRedirectURL: redirectUrl,
      resetPasswordExpirationMinutes: MINUTES_IN_HALF_HOUR,
      resetPasswordRedirectURL: resetUrl,
    },
    // Order in array determines order in UI
    products: oauthOnly
      ? [Products.oauth]
      : [Products.oauth, Products.emailMagicLinks, Products.passwords],
  };
};

export const TOKEN_TO_API: IDictionary = {
  magic_links: 'magicLinks',
  oauth: 'oauth',
  password_authenticate: 'session',
  password_create: 'session',
  reset_password: 'session',
};

export const AUTH_ERROR_MESSAGES: IDictionary = {
  // Generic fallback
  backend_cookie_handoff_failed:
    'We couldn’t finish signing you in. Please try again.',
  failed: 'Failed to authenticate, please try again',
  invalid_credentials: 'Incorrect email or password. Please try again.',
  magic_link_already_used:
    'This link has already been used. Please request a new one.',
  magic_link_expired: 'This link has expired. Please request a new one.',
  misconfigured: 'Misconfigured Stytch',
  oauth_error: 'OAuth authentication failed. Please try again.',
  oauth_token_invalid:
    'OAuth authentication failed. Please try signing in again.',
  password_reset_required: 'Please reset your password to continue.',
  password_reset_succeeded_signin_required:
    'Your password was reset. Please sign in to continue.',
  partner_session_expired: 'Your session has expired. Please sign in again.',
  password_strength_insufficient:
    'Password does not meet security requirements. Please choose a stronger password.',
  session_expired: 'Your session has expired. Please sign in again.',
  session_invalid: 'Invalid session. Please sign in again.',
  sign_in_link_unsupported:
    'This sign-in link is no longer supported. Please sign in with your password or Google/Microsoft.',
  token_expired: 'This link has expired. Please request a new one.',
  too_many_requests: 'Too many attempts. Please wait a moment and try again.',
  unable_to_auth_magic_link:
    'This link has expired. Please request a new one. If you keep having trouble, contact concierge@mento.co.',
  unauthorized_credentials: 'Incorrect email or password. Please try again.',
  unknown:
    'Something went wrong. Please try again or contact support if this persists.',
};
